import { Injectable } from "@angular/core";
import { ArrayHelper, DateHelper, NumberHelper } from "apps/idl/src/helpers";
import { EDatabaseRole, IDataSource, UserData } from "apps/idl/src/model";
import { Acte } from "apps/idl/src/model/Acte";
import { EEtatRegle } from "apps/idl/src/model/EEtatRegle";
import { EMajorationType } from "apps/idl/src/model/EMajorationType";
import { EPlace } from "apps/idl/src/model/EPlace";
import { ESecteur } from "apps/idl/src/model/ESecteur";
import { EStatusSeance } from "apps/idl/src/model/EStatusSeance";
import { IActeDocumentByLc } from "apps/idl/src/model/IActeDocumentByLc";
import { IDeplacementByProfession } from "apps/idl/src/model/IDeplacementByProfession";
import { Majoration } from "apps/idl/src/model/Majoration";
import { EligibilityResult } from "apps/idl/src/model/EligibilityResult";
import { Regle } from "apps/idl/src/model/Regle";
import { Traitement } from "apps/idl/src/model/Traitement";
import { ActesService } from "apps/idl/src/modules/actes/actes.service";
import { IPatient } from "apps/idl/src/modules/patients/model/IPatient";
import { Deplacement } from "apps/idl/src/modules/traitement/model/Deplacement";
import { EIndemniteType } from "apps/idl/src/modules/traitement/model/EIndemniteType";
import { Indemnite } from "apps/idl/src/modules/traitement/model/Indemnite";
import { IdlApplicationService } from "apps/idl/src/services/idlApplicationService.service";
import { IndemniteService } from "apps/idl/src/services/indemnite.service";
import { ContactsService, Store } from "apps/idl/src/services/security";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { EPathologie } from "../../../../model/EPathologies";
import { ITraitement } from "../../../../model/ITraitement";
import { StoredSeance } from "../../../models/StoredSeance";
import { C_BSI_ELIGIBLE_KEY_LETTERS, C_IFI_ELIGIBLE_KEY_LETTERS } from "../../../shared/seances.constants";
import { EEtatActe } from "../enums/EEtatActe";
import { SeanceService } from "./seance.service";
import { SeancesGeneratorService } from "./seances-generator.service";
import { TAX_ALLOWANCE_MESSAGES } from "../../../shared/tax-allowance.constants";
import { ACT_IDS } from "../../../shared/acts.constants";
import { IConfigurationTauxAbattementIK, ITauxAbattementIK } from "../../../models/IConfigurationTauxAbattementIK";

export interface ValidationResult {
    isValid: boolean;
    errors: Record<string, string>;
}

/**
 * Service contenant la logique métier liée à la gestion des séances.
 *
 * Il centralise les opérations permettant de réaliser ou annuler une séance,
 * de modifier son statut (réalisée, annulée, indisponible, etc.), et d'appliquer les
 * règles métier associées.
 *
 * Le service prend également en charge le calcul de l’abattement des actes,
 * l’application des majorations et indemnités.
 */
@Injectable({
    providedIn: "root"
})
export class SeanceRealisationService {

    private readonly ACTES_NON_ELIGIBLE_NON_BLOQUANT_MCI = ['act_0012', 'act_0013', 'act_0552', 'act_0553', 'act_0554'];
    private readonly ACTES_BLOQUANTS_NUIT_DIM = ['act_0411', 'act_0428','act_0546','act_0545','act_0548', 'act_0552', 'act_0553', 'act_0554'];
    private readonly ACTES_AMI_INCOMPATIBLES_BSI = ['act_0391', 'act_0392','act_0426','act_0526','act_0527','act_0528'];
    private readonly LETTRES_CLE_INCOMPATIBLES_BSI = ['AIS', 'TLL', 'TLS', 'TLD', 'TMI', 'RDI', 'RQD', 'PAI'];

    constructor(
        private svcSeanceGenerator: SeancesGeneratorService,
        private svcIndemnite: IndemniteService,
        private svcApplication: IdlApplicationService,
        private svcSeance: SeanceService,
        private svcActe: ActesService,
        private svcStore: Store
    ) { }

    public setActesDone(seances: StoredSeance[]): void {
        seances.forEach((seance: StoredSeance) => {
            seance.actes.forEach((acte: Acte) => {
                acte.etat = EEtatActe.done;
            })
        })
    }

    public getIkIndemnite(patient: IPatient): Indemnite {
        if (patient.lastIkUpdate) {
            return Indemnite.getInstanceFromData(patient.lastIkUpdate)
        } else {
            return new Indemnite("IK-1-1", this.svcIndemnite.getIndemniteTypeFromId("IK-1-1"), 'indemnité kilométrique Plaine', '',);
        }
    }

    public getDeplacement(indemnite?: Indemnite): Deplacement {
        let deplacement = new Deplacement();
        deplacement.isManualPrice = false
        if (!indemnite) {
            deplacement.sectorType = ESecteur.Plaine;
        } else {
            deplacement.sectorType = this.svcIndemnite.getSecteur(indemnite.type);
            deplacement.distance = indemnite.distance;
        }
        return deplacement;
    }

    // Retourne la distance parcourue après abattement
    public getAbattement(deplacement: Deplacement): number {
        return this.svcIndemnite.calculateAbattementDistance(deplacement);
    }

    public initializeIndemnitesForfaitairesList(dateFacturation: Date): Observable<Indemnite[]> {
        return this.svcIndemnite.getDeplacementsByProfession(this.svcApplication.profession).pipe(
            map((deplacements: IDeplacementByProfession[]) => {
                return deplacements
                    .filter(deplacement => !deplacement.lettreCle.includes(EIndemniteType.IK))
                    .map(deplacement => new Indemnite(
                        deplacement.id,
                        this.svcIndemnite.getIndemniteTypeFromId(deplacement.id),
                        deplacement.description,
                        '',
                        this.svcIndemnite.getDeplacementTarif(deplacement, dateFacturation)
                    ));
            })
        );
    }

    public initializeMajorationsList(): Observable<Majoration[]> {
        return this.svcSeanceGenerator.getMajorations().pipe(
            map((majorations: IActeDocumentByLc[]) => {
                return majorations.map((majoration: IActeDocumentByLc) => {
                    const type = this.svcSeanceGenerator.getMajorationTypeFromId(majoration.id);
                    return new Majoration(
                        majoration.id,
                        type,
                        this.svcSeanceGenerator.getMajorationPriceFromArray([majoration], type),
                        this.svcSeanceGenerator.getMajorationDescriptionFromArray([majoration], type),false,false);
                });
            })
        )
    }

    // True si l'IFI est éligible pour la séance
    private isIFIEligible(seance: StoredSeance, dailySeances: StoredSeance[], actesRealises?: Acte[]): EligibilityResult {
        if(!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const regles: Regle[] = [];
        const hasElements = ArrayHelper.hasElements(actesRealises);

        const eligibleIFI = ArrayHelper.unique(actesRealises.map((acte: Acte) => acte.keyLetters))
            .some((psKeyLetter: string) => C_IFI_ELIGIBLE_KEY_LETTERS.some((psEligible: string) => psEligible === psKeyLetter));
        
        regles.push({
            description: "Déplacement lié à la dépendance ou par une IPA.",
            etat: (hasElements && eligibleIFI) ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        const containsActIFI = actesRealises.some(acte => acte.keyLetters === "IFI");
        regles.push({
            description: "Aucun acte IFI 0.01 n'est déjà appliqué.",
            etat: containsActIFI ? EEtatRegle.invalide : EEtatRegle.valide,
        });

        const containsIndemniteIFI = seance.status === EStatusSeance.done && seance.indemnites.some(indemnite => indemnite.type === EIndemniteType.IFI);

        const dailyIFICount = dailySeances
            .filter((s) => s.status === EStatusSeance.done || s.status === EStatusSeance.completed)
            .flatMap(s => s.indemnites ?? [])
            .filter(i => i.type === EIndemniteType.IFI).length;

        const isQuotaOk = containsIndemniteIFI ? dailyIFICount <= 4 : dailyIFICount < 4;
        regles.push({
            description: "Moins de 4 IFI ont déjà été facturés sur la journée.",
            etat: isQuotaOk ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        return {
            eligible: hasElements && eligibleIFI && !containsActIFI && isQuotaOk,
            regles
        };
    }

    public getReglesIk(): Regle[] {
        return [
            {
                description: "Domicile hors de l'agglomération du cabinet et distance > 2km en plaine ou 1km en montagne.",
                etat: EEtatRegle.information,
            },
            {
                description: "Si un cabinet infirmier est situé plus près du domicile, celui-ci est retenu comme référence (sauf IPA).",
                etat: EEtatRegle.information,
            }
        ];
    }

    // True si l'IK est éligible pour la séance
    public isIKEligible(seance: StoredSeance): EligibilityResult {
        const regles: Regle[] = this.getReglesIk();
        
        // L'acte ne peut pas être unique et un IFI 0.01
        return {
            eligible: seance.mePlace === EPlace.home && ((seance.actes.length !== 1 && seance.actes.filter(a => a.etat === EEtatActe.done)).length > 0 || seance.actes[0].id !== ACT_IDS.IFI_0p01),
            regles
        };
    }

    // True si l'IFD est éligible pour la séance
    private isIFDEligible(seance: StoredSeance, actesRealises: Acte[], eligibleIFI: boolean): EligibilityResult {
        if(!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const regles: Regle[] = [];
        const hasElements = ArrayHelper.hasElements(actesRealises);

        const actesDependance = new Set([...C_IFI_ELIGIBLE_KEY_LETTERS, "IFI"]);
        const dependanceOuIPA = actesRealises.some(acte => actesDependance.has(acte.keyLetters));

        regles.push({
            description: "Déplacement non lié à la dépendance ni par une IPA.",
            etat: (hasElements && !dependanceOuIPA) ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Non cumulable avec l’IFI.",
            etat: EEtatRegle.information
        });

        return {
            eligible: hasElements && !dependanceOuIPA && !eligibleIFI,
            regles
        };
    }

    // Retourne la liste des indemnités par défaut pour une séance donnée
    public getSelectedIndemnities(seance: StoredSeance, patient: IPatient, indemnitesForfaitairesList: Indemnite[], actesRealises?: Acte[], dailySeances?: StoredSeance[], IKSelected?: Indemnite): Indemnite[] {
        const { status, indemnites, mePlace } = seance;
        if(!IKSelected) IKSelected = this.getIkIndemnite(patient);
        let selectedIndemnites: Indemnite[] = [];
        if (status === EStatusSeance.done) {
            selectedIndemnites = indemnites ? indemnites.map(Indemnite.getInstanceFromData) : [];
        } else {
            selectedIndemnites.push(IKSelected);
        }

        const indemniteEligibility = this.getIndemniteEligibility(seance, dailySeances, actesRealises);
        
        if (!indemniteEligibility.IK.eligible) {
            selectedIndemnites = selectedIndemnites.filter((indem: Indemnite) => !indem.isIKType)
        }
        
        indemnitesForfaitairesList.forEach((indemnite) => {
            if(indemnite.type !== EIndemniteType.IFI){
                indemnite.disabled = !indemniteEligibility[indemnite.type].eligible;
            }
            indemnite.regles = indemniteEligibility[indemnite.type].regles;
            if (status === EStatusSeance.done || mePlace !== EPlace.home) return;
            if(indemniteEligibility[indemnite.type]?.eligible){
                selectedIndemnites.push(indemnite);
            }
        })
        return selectedIndemnites;
    }

    private isMajorationEligible(seance: StoredSeance, majoration: EMajorationType): EligibilityResult {
				let actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        return {eligible: !Array.isArray(actesRealises) || actesRealises.every(acte => acte.etat === EEtatActe.done && !acte.excludeMajorations?.includes(majoration))};
    }

    private isMIEEligible(seance: StoredSeance, agePatient: number,actesRealises?:Acte[]): EligibilityResult {
		const regles: Regle[] = [];

        if (!this.isMajorationEligible(seance, EMajorationType.Mie)) {
            return {eligible: false};
        }

		if (!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const hasActes = actesRealises.length > 0;

        const isChild = NumberHelper.isValid(agePatient) && agePatient < 7;
				regles.push({
            description: "Age < 7 ans.",
            etat: isChild ? EEtatRegle.valide : EEtatRegle.invalide,
        });

		const notPAI = hasActes && actesRealises.every((acte) => acte.keyLetters !== 'PAI');
        regles.push({
            description: "Tout acte infirmier sauf PAI",
            etat: notPAI ? EEtatRegle.valide : EEtatRegle.invalide,
        });

		regles.push({
            description: "Non cumulable avec la MIP",
            etat: EEtatRegle.information,
        });

		const eligible = isChild && notPAI

        return {eligible,regles};
    }

    /**
     * Détermine si la séance est éligible à la Majoration de Coordination Infirmière (MCI).
     *
     * Conditions principales :
     * - Soins réalisés à domicile
     * - Pansement lourd et complexe ou soins palliatifs
     */
    private isMCIEligible(seance: StoredSeance, patient: IPatient, actesRealises?: Acte[]): EligibilityResult {
        const regles: Regle[] = [];

        if (!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const hasActes = actesRealises.length > 0;

        const soinsADomicile = seance.mePlace === EPlace.home;
        regles.push({
            description: "Soins réalisés à domicile.",
            etat: soinsADomicile && hasActes ? EEtatRegle.valide : EEtatRegle.invalide,
        });
    
        const hasActeBloquant = hasActes && actesRealises.some(a => a.excludeMajorations?.includes(EMajorationType.Mci));
    
        const patientSoinsPalliatifs = hasActes && patient.pathologies?.includes(EPathologie.palliatif)
        let eligiblePansementLourdComplexe = false;
        let eligibleSoinsPalliatifs = false;

        if(patientSoinsPalliatifs){
            eligibleSoinsPalliatifs = actesRealises.some(a => !this.ACTES_NON_ELIGIBLE_NON_BLOQUANT_MCI.includes(a._id));
        }else{
            eligiblePansementLourdComplexe = hasActes && actesRealises.some(a => a.isMciEligible);
        }
        
        regles.push({
            description: "Pansement lourd et complexe éligible OU Soins palliatifs (quel que soit l'acte du titre XVI)",
            etat: eligiblePansementLourdComplexe || eligibleSoinsPalliatifs ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Aucun acte ne bloque la majoration (AMI 11, PAI, RDI, RQD)",
            etat: !hasActeBloquant ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Non cumulable avec la MAU",
            etat: EEtatRegle.information,
        });

        const eligible = (eligiblePansementLourdComplexe || eligibleSoinsPalliatifs) && !hasActeBloquant && soinsADomicile;
        return {eligible, regles};
    }


    // Eligible si l'acte est isolé est de coefficient inférieur à 1.5
    // actesRealises doit être valorisé quand on réalise plusieurs séances d'un coup
    private isMAUEligible(seance: StoredSeance, eligibleIFI: boolean, actesRealises?: Acte[]): EligibilityResult {
        const regles: Regle[] = [];

        if(!actesRealises) actesRealises = seance.actes.filter((acte) => acte.etat === EEtatActe.done);
        
        const isActeUnique = actesRealises.length === 1;
        regles.push({
            description: "Un seul acte réalisé",
            etat: isActeUnique ? EEtatRegle.valide : EEtatRegle.invalide,
        });
        
        const acteRealise = actesRealises[0];
        const isAMI = acteRealise?.keyLetters.toUpperCase() === 'AMI';
        regles.push({
            description: "Acte de type AMI",
            etat: isAMI ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        const coeffActeValid = acteRealise?.priceCoefficient <= 1.5 
        regles.push({
            description: "Coefficient <= 1.5",
            etat: coeffActeValid ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Non cumulable avec la MCI",
            etat: EEtatRegle.information,
        });
        
        const eligible = isActeUnique && coeffActeValid && isAMI && !eligibleIFI

        return {eligible, regles};
    }

    private isMIPEligible(seance: StoredSeance, patient: IPatient, agePatient: number): EligibilityResult {
        if (!this.isMajorationEligible(seance, EMajorationType.Mip)) {
            return {eligible: false};
        }

        const isChildOrElderly = agePatient !== null && (agePatient < 7 || agePatient >= 80);        
        const actesRealises = seance.actes.filter((acte) => acte.etat === EEtatActe.done);
        const containsActeBloquant = actesRealises.some(acte => ['DI', 'BSA', 'BSB', 'BSC', 'AMX', 'IFI'].includes(acte.keyLetters));
        const containsPAI = actesRealises.some(acte => acte.keyLetters === "PAI");
        return {eligible: isChildOrElderly && !containsActeBloquant && containsPAI};
    }

    private isN1Eligible(seance: StoredSeance, heureRealisation: string,actesRealises?: Acte[]): EligibilityResult {
        const regles: Regle[] = [];

        if (!this.isMajorationEligible(seance, EMajorationType.NightFirstHalf)) {
            return {eligible: false};
        }

        if (!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const hasActes = actesRealises.length > 0;

        const [hours, minutes] = heureRealisation.split(':').map(Number);

        let eligibleNuitDimActe = !actesRealises.some(a => this.ACTES_BLOQUANTS_NUIT_DIM.includes(a._id));				
        let eligibleNuitHorraire = (hours >= 5 && hours < 8) || (hours >= 20 && hours < 23)

        regles.push({
            description: "Séance réalisée entre 20h et 23h ou 5h et 8h",
            etat: eligibleNuitHorraire ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Aucun acte ne bloque la majoration (Forfait organisation surveillance perfusions, séance BPCO, PAI 3-6-5)",
            etat: eligibleNuitDimActe ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Non cumulable avec la majoration dimanche/JF",
            etat: EEtatRegle.information,
        });

        return {eligible: hasActes && eligibleNuitDimActe && eligibleNuitHorraire ,regles};
    }


    private isN2Eligible(seance: StoredSeance, heureRealisation: string,actesRealises?: Acte[]): EligibilityResult {
        const regles: Regle[] = [];

        if (!this.isMajorationEligible(seance, EMajorationType.NightSecondHalf)) {
            return {eligible: false};
        }
        const [hours, minutes] = heureRealisation.split(':').map(Number);
        
        if (!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const hasActes = actesRealises.length > 0;

        let eligibleNuitDimActe = !actesRealises.some(a => this.ACTES_BLOQUANTS_NUIT_DIM.includes(a._id));				
        let eligibleNuitHorraire = hours >= 23 || hours < 5

        regles.push({
            description: "Séance réalisée entre 23h et 5h",
            etat: eligibleNuitHorraire ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Aucun acte ne bloque la majoration (Forfait organisation surveillance perfusions, séance BPCO, PAI 3-6-5)",
            etat: eligibleNuitDimActe ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Non cumulable avec la majoration dimanche/JF",
            etat: EEtatRegle.information,
        });

        return {eligible: hasActes && eligibleNuitDimActe && eligibleNuitHorraire ,regles};
    }

    private isDimEligible(seance: StoredSeance,heureRealisation: string,actesRealises?: Acte[]): EligibilityResult {
        const regles: Regle[] = [];

        if (!this.isMajorationEligible(seance, EMajorationType.SundayAndHolyday)) {
            return {eligible: false};
        }

        if (!actesRealises) actesRealises = seance.actes.filter(a => a.etat === EEtatActe.done);
        const hasActes = actesRealises.length > 0;

        const [hours, minutes] = heureRealisation.split(':').map(Number);


        let eligibleNuitDimActe = !actesRealises.some(a => this.ACTES_BLOQUANTS_NUIT_DIM.includes(a._id));	
        let dateSeance = new Date(seance.startDate)			
        let eligibleDimHorraire = (this.svcSeanceGenerator.isSundayOrPublicHoliday(dateSeance))
        let eligibleUrgHorraire = (dateSeance.getDay() === 6 && hours >= 8)


        regles.push({
            description: "Dimanche ou Jour férié",
            etat: eligibleDimHorraire ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Aucun acte ne bloque la majoration (Forfait organisation surveillance perfusions, séance BPCO, PAI 3-6-5)",
            etat: eligibleNuitDimActe ? EEtatRegle.valide : EEtatRegle.invalide,
        });

        regles.push({
            description: "Non cumulable avec la majoration de nuit",
            etat: EEtatRegle.information,
        });

        regles.push({
            description: "Applicable dès samedi 08h aux urgences",
            etat: EEtatRegle.information,
        });

        return {eligible: hasActes && eligibleNuitDimActe && (eligibleDimHorraire || eligibleUrgHorraire) ,regles};
    }

    public getSelectedMajorations(seance: StoredSeance, patient: IPatient, majorationsList: Majoration[], heureRealisation: string, actesRealises?: Acte[], dailySeances?: StoredSeance[]): Majoration[] {
        let selectedMajorations: Majoration[] = [];
        if (seance.status === EStatusSeance.done) {
            selectedMajorations = [...seance.majorations];
        }
        if(seance.actes.length === 0) return selectedMajorations
        const eligibleIFI: boolean = this.isIFIEligible(seance, dailySeances, actesRealises).eligible;
        const agePatient: number = this.getAgePatient(seance, patient);

        const majorationEligibility = {
            Dim: this.isDimEligible(seance,heureRealisation),
            MIE: this.isMIEEligible(seance, agePatient),
            MCI: this.isMCIEligible(seance, patient, actesRealises),
            MAU: this.isMAUEligible(seance, eligibleIFI, actesRealises),
            MIP: this.isMIPEligible(seance, patient, agePatient),
            N1: this.isN1Eligible(seance, heureRealisation),
            N2: this.isN2Eligible(seance, heureRealisation)
        }

        const eligibleMipOrNotDim: boolean = majorationEligibility[EMajorationType.Mip].eligible || !majorationEligibility[EMajorationType.SundayAndHolyday].eligible;
        const sundayOrHoliday: boolean = eligibleMipOrNotDim ? false : this.svcSeanceGenerator.isSundayOrPublicHoliday(new Date(seance.startDate));

        majorationsList = majorationsList.map((majoration) => {            
            majoration.disabled = !majorationEligibility[majoration.type].eligible;
            majoration.regles = majorationEligibility[majoration.type].regles;

            if (seance.status === EStatusSeance.to_be_done &&
                (
                    (majoration.type === EMajorationType.Mie && majorationEligibility[EMajorationType.Mie].eligible) ||
                    (majoration.type === EMajorationType.SundayAndHolyday && !majorationEligibility[EMajorationType.NightFirstHalf].eligible && !majorationEligibility[EMajorationType.NightSecondHalf].eligible && majorationEligibility[EMajorationType.SundayAndHolyday] && sundayOrHoliday) ||
                    (majoration.type === EMajorationType.Mci && majorationEligibility[EMajorationType.Mci].eligible) ||
                    (majoration.type === EMajorationType.NightFirstHalf && majorationEligibility[EMajorationType.NightFirstHalf].eligible) ||
                    (majoration.type === EMajorationType.NightSecondHalf && majorationEligibility[EMajorationType.NightSecondHalf].eligible) ||
                    (majoration.type === EMajorationType.Mau && majorationEligibility[EMajorationType.Mau].eligible && !eligibleIFI && !majorationEligibility[EMajorationType.Mci].eligible) ||
                    (majoration.type === EMajorationType.Mip && majorationEligibility[EMajorationType.Mip].eligible && !majorationEligibility[EMajorationType.Mie].eligible)
                )
            ) {
                selectedMajorations.push(majoration);
            } else if (majoration.disabled) {
                selectedMajorations = selectedMajorations.filter(a => a.type !== majoration.type);
            }
            return majoration;
        });
        return selectedMajorations;
    }

    

    private getAgePatient(seance: StoredSeance, patient: IPatient): number {
        const birthDateStr = patient?.birthDate;
        const isValidBirthDate = birthDateStr && DateHelper.isDate(birthDateStr);
        if (!isValidBirthDate) return null;

        const birthDate = new Date(birthDateStr);
        const seanceStartDate = new Date(seance.startDate);
        return DateHelper.calculateAge(birthDate, seanceStartDate);
    }

    // Récupère les indemnités de déplacement de la profession "infirmier libéral"
    public getDeplacementsByProfession(): Observable<IDeplacementByProfession[]> {
        return this.svcIndemnite.getDeplacementsByProfession(this.svcApplication.profession)
    }

    public setSeanceToBeDone(seance: StoredSeance): StoredSeance {
        if(seance.status === EStatusSeance.completed) return seance;
        seance.actes = seance.actes.map((acte: Acte) => {
            const clonedActe = new Acte(acte);
            clonedActe.seanceId = seance._id;
            clonedActe.etat = EEtatActe.to_be_done;
            clonedActe.taxAllowance = 1;
            delete clonedActe.isAbattementForced;
            delete clonedActe.taxAllowanceForced;
            if (clonedActe.keyLetters === "AMX" && clonedActe.isAmx) {
                clonedActe.keyLetters = "AMI";
            }
            clonedActe.calculatePrice();
            return clonedActe;
        });
        seance.majorations = [];
        seance.indemnites = [];
        seance.status = EStatusSeance.to_be_done;
        seance.isSeanceDependance = false;
        delete seance.statusChangeDate;
        delete seance.concurrentSeanceId;
        delete seance.commentaireIndisponibilitePatient;
        return seance;
    }

    public setSeancesDone(
        seance: StoredSeance,
        heureRealisation: string,
        majorations: Majoration[],
        indemnites: Indemnite[],
        patient: IPatient,
        concurrentSeances?: StoredSeance[]
    ): StoredSeance[] {
        delete seance.concurrentSeanceId;
        seance = this.annulerPatientIndispo(seance);
        seance = this.annulerPause(seance);
        const statusChangeDate = new Date();
        seance.statusChangeDate = statusChangeDate;
        seance.status = EStatusSeance.done;
        if (seance.actes.some((acte: Acte) => acte.etat === EEtatActe.done)) {
            seance.concurrentSeanceId = seance._id;
        }

        if (heureRealisation) {
            const [hours, minutes] = heureRealisation.split(':').map(Number);
            const dateSeance = new Date(seance.startDate);
            dateSeance.setHours(hours, minutes);
            seance.startDate = dateSeance;
        }

        const infirmierIdConnecte: string = ContactsService.getContactIdFromUserId(UserData.current._id);
        if (!seance.infirmierId) {
            seance.infirmierId = infirmierIdConnecte;
        }

        seance.majorations = [];
        seance.indemnites = [];

        if (concurrentSeances) {
            concurrentSeances.forEach((seanceChild: StoredSeance) => {
                delete seanceChild.concurrentSeanceId;
                seanceChild.majorations = [];
                seanceChild.indemnites = [];
                if (seanceChild.actes.some((acte: Acte) => acte.etat === EEtatActe.done)) {
                    seanceChild.concurrentSeanceId = seance._id;
                    if (!seanceChild.infirmierId) {
                        seanceChild.infirmierId = infirmierIdConnecte;
                    }
                    seanceChild.statusChangeDate = statusChangeDate;
                    seanceChild.status = EStatusSeance.done;
                    seanceChild.startDate = seance.startDate;
                }
            });
        }

        const seanceActesGratuits = seance.actes.every(a => a.etat === EEtatActe.done && a.price === 0);

        let seancePourIndemnitesMajorations = seance;
        if (seanceActesGratuits && concurrentSeances) {
            const autreSeance = concurrentSeances.find(s =>
                s.actes.some(a => a.etat === EEtatActe.done && a.price > 0)
            );
            if (autreSeance) {
                seancePourIndemnitesMajorations = autreSeance;
            }
        }

        seancePourIndemnitesMajorations.majorations = majorations.map(m =>
            new Majoration(m.id, m.type, m.price, m.description, m.addedByUser, m.disabled)
        );
        indemnites.sort((a, b) => a.type.localeCompare(b.type));

        const IK: Indemnite = indemnites.find((indemnite: Indemnite) => indemnite.isIKType);
        seancePourIndemnitesMajorations.indemnites = IK && (!IK.distance || IK.disabled)
            ? indemnites.filter((indemnite: Indemnite) => !indemnite.isIKType)
            : indemnites;

        return [seance, ...(concurrentSeances ?? [])];
    }

    public annulerPatientIndispo(seance: StoredSeance): StoredSeance {
        delete seance.commentaireIndisponibilitePatient;
        return seance;
    }

    public annulerPause(seance: StoredSeance): StoredSeance {
        delete seance.dateDebutPause;
        delete seance.dateFinPause;
        delete seance.commentairePause;
        return seance;
    }

    public initializeActesList(seances: StoredSeance[]) {
        const actesIds: string[] = ArrayHelper.unique(ArrayHelper.flat(ArrayHelper.flat(seances.map((seance: StoredSeance) =>
            seance.actes.map((acte: Acte) => acte._id ?? [])))));

        return this.svcActe.getActes(actesIds).pipe(
            switchMap((actes: Acte[]) => {
                // On récupère les actes dans le référentiel pour mettre à jour les actes dans la seance
                let cotationRulesIds: string[] = [];
                actes.forEach((acte: Acte) => {
                    cotationRulesIds.push(...(acte.cotationRulesIds ?? []));
                    seances.forEach((seance: StoredSeance) => {
                        seance.actes.forEach((seanceActe: Acte) => {
                            if (seanceActe.id === acte.id) {
                                seanceActe.cotationRulesIds = ArrayHelper.unique(acte.cotationRulesIds ?? []);
                                seanceActe.isPackage = acte.isPackage;
                                seanceActe.excludeMajorations = acte.excludeMajorations;
                                seanceActe.excludeFromTaxAllowance = acte.excludeFromTaxAllowance;
                            }
                        });
                    });
                })
                return this.svcActe.getCotationRules(ArrayHelper.unique(cotationRulesIds));
            })
        )

    }

    public realiserSeancesSilent(seances: StoredSeance[], patient: IPatient, ordonnance: ITraitement): Observable<Map<string, {seance: StoredSeance, isDone: boolean, erreur?: string}>> {
        const mapSeances = new Map();
        seances = seances.filter((seance: StoredSeance) => seance.status !== EStatusSeance.done && seance.status !== EStatusSeance.completed);
        if (seances.length === 0) return of(mapSeances);

        let seancesByDate = new Map<string, StoredSeance[]>();
        let indemnitesList: Indemnite[];
        let majorationsList: Majoration[];
        
        return this.svcSeance.getDailySeancesMapByDate(seances, patient._id, true).pipe(
            map(mapSeancesByDate => {
                seancesByDate = mapSeancesByDate;
                return seances.filter(seance => {
                    const seanceValidation = this.getSeanceValidation(seance.actes, seancesByDate.get(DateHelper.resetDay(new Date(seance.startDate)).toISOString()));
                    const countErreursSaisie = Object.keys(seanceValidation.errors).length;
                    if (countErreursSaisie > 0) {
                        mapSeances.set(seance._id, {
                            seance,
                            isDone: false,
                            erreur:  `${countErreursSaisie} erreur${countErreursSaisie > 1 ? "s" : ""} de saisie à corriger.`
                        });
                    }
                    return countErreursSaisie === 0;
                })
            }),
            switchMap((validSeances: StoredSeance[]) => {
                if(validSeances.length === 0) return of(mapSeances)
                return forkJoin({
                    indemnites: this.initializeIndemnitesForfaitairesList(new Date()),
                    majorations: this.initializeMajorationsList()
                }).pipe(
                    tap(({ indemnites, majorations }) => {
                        indemnitesList = indemnites;
                        majorationsList = majorations;
                        this.setActesDone(validSeances);
                    }),
                    switchMap(() => ordonnance.isHADSSIAD ? of(validSeances) : this.svcSeanceGenerator.applyTaxAllowance(validSeances, patient, seancesByDate)),
                    map((seancesUpdated: StoredSeance[]) => {
                        seancesUpdated.forEach((seance: StoredSeance) => {
                            seance.isSeanceDependance = this.isSeanceDependance(seance.actes);
                            const heureRealisation: string = DateHelper.getHoursAndMinutes(seance.startDate);
                            const selectedMajorations: Majoration[] = this.getSelectedMajorations(seance, patient, majorationsList, heureRealisation, null, seancesByDate.get(DateHelper.resetDay(new Date(seance.startDate)).toISOString()));
                            const selectedIndemnites: Indemnite[] = this.getSelectedIndemnities(seance, patient, indemnitesList, null, seancesByDate.get(DateHelper.resetDay(new Date(seance.startDate)).toISOString()));
                            mapSeances.set(seance._id, {seance: ArrayHelper.getFirstElement(this.setSeancesDone(seance, heureRealisation, selectedMajorations, selectedIndemnites, patient)), isDone: true});
                        })
                        return mapSeances
                    })
                )
            }),
        );
    }

    // En cas d'annulation de séance avec BSI payant, on tente de trouver une séance avec un BSI gratuit pour la mettre à jour
    private updateBSITaxAllowance(cancelledSeances: StoredSeance[]): Observable<StoredSeance[]>{
        if(cancelledSeances.length === 0) return of([]);
        const patientId = cancelledSeances[0].patientId;
        const lostPaidBsiSeances = cancelledSeances.filter(s => 
            s.actes.some(a => C_BSI_ELIGIBLE_KEY_LETTERS.includes(a.keyLetters) && a.taxAllowance !== 0)
        );

        if (lostPaidBsiSeances.length === 0) return of([]);

        const uniqueDates = [...new Set(lostPaidBsiSeances.map(s => 
            DateHelper.resetDay(new Date(s.startDate)).toISOString()
        ))];

        const $seancesUpdate = uniqueDates.map(dateStr => {
            return this.svcSeance.selectSeancesByDateAndPatient(new Date(dateStr), patientId, true).pipe(
                map(dailySeances => {
                    const candidate = dailySeances.find(s => 
                        !cancelledSeances.some(cs => cs._id === s._id) &&
                        (s.status === EStatusSeance.done) &&
                        s.actes.some(a => C_BSI_ELIGIBLE_KEY_LETTERS.includes(a.keyLetters) && a.taxAllowance === 0 && a.etat === EEtatActe.done)
                    );

                    if (candidate) {
                        const actBsi = candidate.actes.find(a => C_BSI_ELIGIBLE_KEY_LETTERS.includes(a.keyLetters) && a.taxAllowance === 0 && a.etat === EEtatActe.done);
                        if (actBsi) {
                            actBsi.taxAllowance = 1; 
                            return candidate;
                        }
                    }
                    return null;
                })
            );
        });

        return forkJoin($seancesUpdate).pipe(
            map(results => results.filter((s): s is StoredSeance => s !== null))
        );
    }

    public annulerSeancesSilent(seances: StoredSeance[]): Observable<StoredSeance[]> {
        seances = seances.filter(seance => seance.status !== EStatusSeance.to_be_done && seance.status !== EStatusSeance.completed);
        if (seances.length === 0) return of([]);

        const $allCancelledSeances = forkJoin(seances.map(seance => {
            if (!seance.concurrentSeanceId) {
                return of([seance]);
            }
            return this.svcSeance.selectSeancesByConcurrentSeanceId(seance.concurrentSeanceId)
        })).pipe(
            map(groups => {
                const flatSeancesList = groups.flat();
                const seancesSet = new Set();
                return flatSeancesList.filter(s => {
                    if (seancesSet.has(s._id)) return false;
                    seancesSet.add(s._id);
                    return true;
                });
            })
        );

        return $allCancelledSeances.pipe(
            switchMap((seancesToCancel: StoredSeance[]) => {
                return this.updateBSITaxAllowance(seancesToCancel).pipe(
                    map((promotedSeances: StoredSeance[]) => {
                        const cancelledSeances = seancesToCancel.map(seance => this.setSeanceToBeDone(seance));
                        return [...cancelledSeances, ...promotedSeances];
                    })
                );
            })
        );
    }

    // Met à jour les séances existantes (non facturées) d'un traitement suite à la modification de ce dernier (ALD, HAD/SSIAD)
    public updateTraitementSeances(ordonnance: Traitement, patient: IPatient): Observable<boolean> {
        return this.svcSeance.selectSeancesByTraitement(ordonnance._id).pipe(
            mergeMap((seances: StoredSeance[]) => {
                const doneSeances: StoredSeance[] = seances.filter((seance: StoredSeance) => seance.status === EStatusSeance.done);
                const notDoneSeances: StoredSeance[] = seances.filter((seance: StoredSeance) => seance.status !== EStatusSeance.done);
                notDoneSeances.forEach((seance: StoredSeance) => {
                    seance.actes.forEach((acte: Acte) => {
                        if (acte.isAldExonerante !== ordonnance.isAld) {
                            acte.isAldExonerante = ordonnance.isAld;
                        }
                    })
                });

                const done$ = (ordonnance.isHADSSIAD
                    ? of(doneSeances)
                    : this.svcSeance.getDailySeancesMapByDate(doneSeances, patient._id).pipe(
                        switchMap((mapSeancesByDate) => this.svcSeanceGenerator.applyTaxAllowance(doneSeances, patient, mapSeancesByDate))
                    )
                ).pipe(
                    map((seances: StoredSeance[]) => {
                        return seances.filter((seance) => {
                            let hasChanges = false;
                            seance.actes.forEach((acte: Acte) => {
                                if (acte.isAldExonerante !== ordonnance.isAld) {
                                    acte.isAldExonerante = ordonnance.isAld;
                                }
                                if (ordonnance.isHADSSIAD) {
                                    acte.taxAllowance = 1;
                                }
                                const newActe = new Acte(acte);
                                newActe.calculatePrice();
                                acte.price = newActe.price;
                                hasChanges = true;
                            });
                            return hasChanges;
                        });
                    })
                );
                const notDone$ = of(notDoneSeances);
                return forkJoin([done$, notDone$]).pipe(
                    map(([doneSeances, notDoneSeances]) => {
                        return [...doneSeances, ...notDoneSeances];
                    })
                );
            }),
            mergeMap((updatedSeances: StoredSeance[]) => {
                return updatedSeances.length > 0 ? this.svcSeance.updateSeances(updatedSeances) : of(true);
            })
        );
    }

    public isSeanceDependance(actes: Acte[]): boolean {
        return actes.some((acte) => C_BSI_ELIGIBLE_KEY_LETTERS.includes(acte.keyLetters) || acte._id === ACT_IDS.AIS_3);
    }

    private validateDependanceRealisable(actes: Acte[]): ValidationResult {
        const errors: Record<string, string> = {};
        
        if (!this.isSeanceDependance(actes)) {
            return { isValid: true, errors: {} };
        }

        // Détection du type de forfait présent dans la séance
        const hasBsi = actes.some(a => C_BSI_ELIGIBLE_KEY_LETTERS.includes(a.keyLetters));
        const hasAis = actes.some(a => a._id === ACT_IDS.AIS_3);

        actes.forEach(acte => {
            // Cas AMI sans AMX
            if (acte.keyLetters === "AMI" && !acte.isAmx) {
                errors[acte.guid] = "Cet acte ne possède pas d'équivalent AMX et ne peut donc pas être facturé au cours d'un passage lié à la dépendance";
            }
            
            const isKeyLetterIncompatible = this.LETTRES_CLE_INCOMPATIBLES_BSI.includes(acte.keyLetters);
            if (isKeyLetterIncompatible && (hasBsi || (hasAis && acte.keyLetters !== 'AIS'))) {
                errors[acte.guid] = "Cet acte ne peut pas être facturé au cours d'un passage lié à la dépendance";
            }
        });

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    // Permet de savoir si un BSI serait réalisé le même jour qu'un AMI incompatible si la séance était facturée
    private validateBsiAmiConflicts(actes: Acte[], dailySeances: StoredSeance[]): ValidationResult {
        const errors: Record<string, string> = {};
        
        const currentBsiActes = actes.filter(a => C_BSI_ELIGIBLE_KEY_LETTERS.includes(a.keyLetters) || a._id === ACT_IDS.AIS_3);
        const currentIncompatibleAmiActes = actes.filter(a => this.ACTES_AMI_INCOMPATIBLES_BSI.includes(a.id));


        const hasBsiInDaily = dailySeances.some(s => 
            (s.status === EStatusSeance.done || s.status === EStatusSeance.completed) &&
            s.actes.some(a => (C_BSI_ELIGIBLE_KEY_LETTERS.includes(a.keyLetters) || a._id === ACT_IDS.AIS_3) && a.etat === EEtatActe.done)
        );
        const hasAmiInDaily = dailySeances.some(s => 
            (s.status === EStatusSeance.done || s.status === EStatusSeance.completed) &&
            s.actes.some(a => this.ACTES_AMI_INCOMPATIBLES_BSI.includes(a.id) && a.etat === EEtatActe.done)
        );

        const conflictBsi = currentBsiActes.length > 0 && (currentIncompatibleAmiActes.length > 0 || hasAmiInDaily);
        const conflictAmi = currentIncompatibleAmiActes.length > 0 && (currentBsiActes.length > 0 || hasBsiInDaily);

        if (conflictBsi) {
            currentBsiActes.forEach(a => {
                if(currentIncompatibleAmiActes.length > 0){
                    errors[a.guid] = "Un acte non facturable le même jour qu'un forfait BSI est présent dans la séance";
                }else{
                    errors[a.guid] = "Un acte non facturable le même jour qu'un forfait BSI a déjà été réalisé ce jour";
                }
            });
        }
        if (conflictAmi) {
            currentIncompatibleAmiActes.forEach(a => errors[a.guid] = "Cet acte ne peut être facturé le même jour qu'un forfait BSI");
        }

        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    }

    public getSeanceValidation(actes: Acte[], dailySeances: StoredSeance[]): ValidationResult {
        const depValidation = this.validateDependanceRealisable(actes);
        const bsiAmiValidation = this.validateBsiAmiConflicts(actes, dailySeances);

        return {
            isValid: depValidation.isValid && bsiAmiValidation.isValid,
            errors: { ...depValidation.errors, ...bsiAmiValidation.errors }
        };
    }

    public getTauxAbattementIK(dateToCompare: string |Date): Observable<ITauxAbattementIK> {
        const datasource: IDataSource = {
            databaseId: this.svcStore.getDatabasesIdsByRole(EDatabaseRole.formsEntries)[0],
            viewParams: {
                include_docs: true,
                keys: ["tx_abattements_ik"],
            },
        };
        return this.svcStore.getOne<IConfigurationTauxAbattementIK>(datasource).pipe(
            map((doc: IConfigurationTauxAbattementIK) => {
                return doc.taux.filter(taux => {
                    //la date à comparer doit être comprise entre la date de début et de fin du taux
                    //si la date de fin est null, on considère que le taux est toujours valide s'il se trouve après la date de début
                    if (!taux.dateFin) {
                        return DateHelper.compareTwoDates(new Date(dateToCompare), new Date(taux.dateDebut)) >= 0;
                    }
                    return DateHelper.isBetweenTwoDates(new Date(dateToCompare), new Date(taux.dateDebut), new Date(taux.dateFin));
                });
            }),
            catchError((error) => {
                console.error('Erreur lors de la récupération :', error);
                return of(null);
            })
        );
    }

    public getIndemniteEligibility(seance: StoredSeance, dailySeances: StoredSeance[], actesRealises?: Acte[]) {
        const eligibleIFI: EligibilityResult = this.isIFIEligible(seance, dailySeances, actesRealises);
        return {
            IFI: eligibleIFI,
            IFD: this.isIFDEligible(seance, actesRealises, eligibleIFI.eligible),
            IK: this.isIKEligible(seance)
        };
    }
}